import i18n                                     from '@/lang/index.js';
import Service                                  from '@/api/service';
import { getFirstAndLastName, getKeyValueTrad } from '@/composables/formatData';

export const THIRD_PARTY_INVOICE_STATUS_TODO = 'todo';
export const THIRD_PARTY_INVOICE_STATUS_VALIDATED = 'validated';
export const THIRD_PARTY_INVOICE_STATUS_SENT_TO_RECIPIENT = 'sent_to_recipient';
export const THIRD_PARTY_INVOICE_STATUS_PAID = 'paid';
export const THIRD_PARTY_INVOICE_STATUS_CANCELLED = 'cancelled';

export function useThirdPartyInvoiceStatusList(actualStatus) {

  const messages = i18n.global.messages[i18n.global.locale];

  if (actualStatus === THIRD_PARTY_INVOICE_STATUS_PAID) {

    return [
      { key : THIRD_PARTY_INVOICE_STATUS_PAID, value : messages.thirdPartyInvoice.status.paid, color : 'yellow' },
      { key : THIRD_PARTY_INVOICE_STATUS_CANCELLED, value : messages.thirdPartyInvoice.status.cancelled, color : 'red' }
    ];
  }

  return [
    { key : THIRD_PARTY_INVOICE_STATUS_TODO, value : messages.thirdPartyInvoice.status.todo, color : 'blue-300' },
    { key : THIRD_PARTY_INVOICE_STATUS_VALIDATED, value : messages.thirdPartyInvoice.status.validated, color : 'green' },
    { key   : THIRD_PARTY_INVOICE_STATUS_SENT_TO_RECIPIENT, value : messages.thirdPartyInvoice.status.sent_to_recipient,
      color : 'orange'
    },
    { key : THIRD_PARTY_INVOICE_STATUS_PAID, value : messages.thirdPartyInvoice.status.paid, color : 'yellow' },
    { key : THIRD_PARTY_INVOICE_STATUS_CANCELLED, value : messages.thirdPartyInvoice.status.cancelled, color : 'red' }
  ];
}

/**
 * Third party invoice status is disabled if :
 * - The status option is "to do" and the invoice has another status. (We can't go back to "to do" status)
 * - The invoice is already in "paid" or "cancelled" status, all other status options are disabled.
 * - The fields are undefined and the status option are "validated", "sent_to_recipient" or "paid", and we are
 * changing the status from the dropdown tag in invoice line. If we are changing the status from the form, we can
 * select those options because the user can fill the fields before validation.
 */
export function isThirdPartyInvoiceStatusDisabled(invoice, status, isFromTag) {

  const statusIsTodoButNotSelected = [THIRD_PARTY_INVOICE_STATUS_TODO].includes(status.key) &&
    ![THIRD_PARTY_INVOICE_STATUS_TODO].includes(invoice?.status?.key);

  const isValueDefined = (value) => value !== undefined && value !== null && value !== '';

  const statusPaidOrCancelled = [THIRD_PARTY_INVOICE_STATUS_PAID, THIRD_PARTY_INVOICE_STATUS_CANCELLED];
  const invoiceIsPaid = statusPaidOrCancelled.includes(invoice?.status?.key) &&
    !statusPaidOrCancelled.includes(status?.key);

  const statusRequiredFieldToBeFilled = !(isValueDefined(invoice.customData?.invoiceTitle)
    && isValueDefined(invoice?.customData?.feesHT)
    && isValueDefined(invoice?.customData?.recipient)
    && isValueDefined(invoice?.customData?.address?.line1)
    && isValueDefined(invoice?.customData?.address?.postcode)
    && isValueDefined(invoice?.customData?.address?.town)
    && isValueDefined(invoice?.customData?.address?.country)
    && isValueDefined(invoice?.customData?.vat)
    && isValueDefined(invoice?.bankAccount)
    || [THIRD_PARTY_INVOICE_STATUS_TODO, THIRD_PARTY_INVOICE_STATUS_CANCELLED].includes(status.key));

  return invoiceIsPaid || statusRequiredFieldToBeFilled && isFromTag || statusIsTodoButNotSelected;
}

export async function useFormatThirdPartyInvoice(tenantId, thirdPartyInvoice) {

  let actor = getInvoiceActor(thirdPartyInvoice);

  if (actor.type === 'primaryNotary') {

    actor = await Service.getNotary(tenantId, actor.id);
    if (actor?.lastName && actor?.firstName) {

      actor.name = getFirstAndLastName(actor);
    }
  }

  let bankAccount = undefined;

  try {
    bankAccount = await getBankAccount(tenantId, thirdPartyInvoice.bankAccountId);
  } catch { /* empty */
  }

  return {
    id               : thirdPartyInvoice.id,
    invoiceId        : thirdPartyInvoice.invoiceId,
    dossierId        : thirdPartyInvoice.dossierId,
    dossier          : thirdPartyInvoice.dossier,
    thirdParty       : thirdPartyInvoice.thirdParty,
    status           : getKeyValueTrad('thirdPartyInvoice.status', thirdPartyInvoice.status),
    actor            : actor,
    language         : thirdPartyInvoice.language,
    customData       : thirdPartyInvoice.customData,
    fundsReceiptDate : thirdPartyInvoice.fundsReceiptDate || '',
    documentId       : thirdPartyInvoice.documentId,
    number           : thirdPartyInvoice.number,
    mailId           : thirdPartyInvoice.mailId,
    bankAccount
  };
}

function getInvoiceActor(invoice) {

  if (Array.isArray(invoice.actors) && invoice?.actors?.length > 0) {

    return invoice?.actors.reduce((a, b) => {

      return new Date(a.createdAt) > new Date(b.createdAt) ? a : b;
    });
  }

  if (invoice?.customData.recipient) {

    return { name : invoice?.customData.recipient };
  }

  return {};
}

async function getBankAccount(tenantId, bankAccountId) {

  const bankAccount = await Service.getBankAccount(tenantId, bankAccountId);
  return { key : bankAccount.id, value : `${ bankAccount.bankName } - ${ bankAccount.label }` };
}
